import React from 'react';
import { Dropdown } from 'react-bootstrap';
import PropTypes from 'prop-types';
import User from 'shared/user';
import { BUILDER, DRAFT, SEO_PAGES, PUBLISH_FACILITY_PAGES, PUBLISH_NON_FACILITY_PAGES } from 'shared/abilities';
import { FACILITY_LINK, FACILITY_LISTING, HOME_PAGE } from 'shared/pageTypes';
import { CLONE_ACTION, DRAFT_ACTION, LIVE_ACTION } from '../constants/actions';
import style from './PageListActions.scss';
import { BasicAction, PublishAction, FacilityImageAction, EditAction } from '.';

const PageListActions = ({
  page,
  page: { disableEnUsPage },
  handleEdit,
  handleSettings,
  handleActionModal,
  handleFacilityIdModal,
  profile,
  disabledRow,
  handleDeleteModal,
  handleApplyPageLayout,
}) => {
  const isFacilityPage = !!page.facility;
  const isFacilityLink = [FACILITY_LINK, FACILITY_LISTING].includes(page.metadata.type);

  const user = new User(profile);
  const authProps = {
    action: 'manage',
    companyId: user.companyId,
    facilityId: page.facilityId,
  };

  const basicProps = {
    page,
    onClick: ({ actionName, currPage }) => handleActionModal({ actionName, currPage }),
  };

  const actions = [];

  const isBuilder = user.isAuthorized({ subject: BUILDER, ...authProps });
  const canDraft = user.isAuthorized({ subject: [BUILDER, DRAFT], ...authProps }) && page.live && !disabledRow;

  if (isFacilityLink) {
    actions.push('image');
    if (canDraft) actions.push('draft');
    if (!page.live && isBuilder) actions.push('live');
    if (isBuilder) actions.push('delete');
  } else {
    const canChangeSettings = user.isAuthorized({ subject: [BUILDER, SEO_PAGES], ...authProps }) && !disabledRow;
    const publishSubject = isFacilityPage ? PUBLISH_FACILITY_PAGES : PUBLISH_NON_FACILITY_PAGES;
    const isHyperFacilityPage = isFacilityPage &&
      page.metadata.type !== HOME_PAGE &&
      user.isAuthorized({ requireHyper: true });

    actions.push('edit');
    if (canChangeSettings) actions.push('settings');
    if (isBuilder) actions.push('clone');
    if (isHyperFacilityPage) actions.push('applyPageLayout');
    if (user.isAuthorized({ subject: publishSubject, ...authProps }) && !disableEnUsPage) actions.push('publish');
    if (isBuilder) actions.push('delete');
    if (canDraft) actions.push('draft');
    if (!page.live && isBuilder && !disableEnUsPage) actions.push('live');
    if (isHyperFacilityPage) actions.push('facilityId');
  }

  const actionsProps = {
    draft: {
      ...basicProps,
      actionName: DRAFT_ACTION,
      displayName: 'Make Draft',
    },
    live: {
      ...basicProps,
      actionName: LIVE_ACTION,
      displayName: 'Make Live',
    },
    delete: {
      ...basicProps,
      displayName: 'Delete',
      onClick: () => handleDeleteModal(page),
    },
    clone: {
      ...basicProps,
      actionName: CLONE_ACTION,
      displayName: 'Clone',
    },
    image: {
      component: FacilityImageAction,
      props: { page, disabledRow, key: 'image' },
    },
    facilityId: {
      page,
      displayName: 'Facility ID',
      onClick: () => handleFacilityIdModal(page),
    },
    settings: {
      page,
      displayName: 'Settings',
      onClick: () => handleSettings(page),
    },
    edit: {
      component: EditAction,
      props: { page, disabledRow, onClick: handleEdit(page) },
    },
    publish: {
      component: PublishAction,
      props: { page, handleActionModal, key: 'publish' },
    },
    applyPageLayout: {
      page,
      displayName: 'Apply Page Layout',
      onClick: () => handleApplyPageLayout(page),
    },
  };

  let topBtn;

  if (actions.length) {
    const action = actions.shift();
    const actionProps = actionsProps[action];
    const singleBtn = actions.length === 0;
    topBtn = actionProps.component ? (
      <actionProps.component {...actionProps.props} asTopMenuOpt singleBtn={singleBtn} />
    ) : (
      <BasicAction {...actionProps} asTopMenuOpt singleBtn={singleBtn} key={action} />
    );
  }

  const menu = actions.map((action) => {
    const actionProps = actionsProps[action];

    return actionProps.component ? (
      <actionProps.component {...actionProps.props} />
    ) : (
      <BasicAction {...actionProps} key={action} />
    );
  });

  return (
    <div className={style.actionMenu}>
      {menu.length === 0 ? (
        topBtn
      ) : (
        <Dropdown id="PageListActions">
          {topBtn}
          <Dropdown.Toggle variant="default" className={style.dropdownToggle} />
          <Dropdown.Menu>
            {menu}
          </Dropdown.Menu>
        </Dropdown>
      )}
    </div>
  );
};

PageListActions.propTypes = {
  page: PropTypes.object.isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleSettings: PropTypes.func.isRequired,
  handleActionModal: PropTypes.func.isRequired,
  handleFacilityIdModal: PropTypes.func.isRequired,
  handleDeleteModal: PropTypes.func.isRequired,
  profile: PropTypes.object.isRequired,
  disabledRow: PropTypes.bool.isRequired,
  handleApplyPageLayout: PropTypes.func.isRequired,
};

export default PageListActions;
